import {
  HeartHandshakeIcon,
  LayoutDashboardIcon,
  ListTreeIcon,
  MessagesSquareIcon,
  UsersIcon,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { mag, type Recht } from '@vip/shared';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Logo } from '../../design-system';
import { useAuth } from '../../features/auth/useAuth';

/**
 * De navigatie volgt de rechten. Mijn gesprekken staat er voor iedereen: het
 * lezen zit in `IEDEREEN_MAG`. Het aanleveren blijft aan het buddyvinkje, en dat
 * regelt het scherm zelf: de twee knoppen staan er dan uitgeschakeld bij.
 *
 * Hier stond dat een buddy geen dashboard ziet, "omdat verklappen dat het
 * bestaat al een gesprek uitlokt". Dat is verlaten. Een buddy begeleidt mensen
 * en hoort te zien waar de club tegenaan loopt en hoe de begeleiding ervoor
 * staat. Kijken mag hij overal; wijzigen gaat over zijn eigen rijen, en dat
 * regelt de server.
 *
 * Instellingen staat hier bewust niet tussen: dat is bijstellen, geen werk, en
 * het zit achter het gebruikersmenu in de header.
 */
const ITEMS: Array<{ label: string; pad: string; recht: Recht; icoon: typeof MessagesSquareIcon }> =
  [
    {
      label: 'Mijn gesprekken',
      pad: '/gesprekken',
      recht: 'meeting:read:own',
      icoon: MessagesSquareIcon,
    },
    { label: 'Dashboard', pad: '/overzicht', recht: 'dashboard:read', icoon: LayoutDashboardIcon },
    // Personen is werk en geen bijstellen: hier voeg je mensen toe en houd je de
    // administratie op orde. Vandaar naast het dashboard en niet bij de
    // instellingen, waar het paneel Toegang eerder stond.
    { label: 'Personen', pad: '/personen', recht: 'persoon:read', icoon: UsersIcon },
    // Eigen item en geen tabblad onder Personen: koppelen is een andere taak dan
    // iemand toevoegen, en de begeleiding loopt over de opdrachten heen.
    {
      label: 'Begeleiding',
      pad: '/begeleiding',
      recht: 'persoon:read',
      icoon: HeartHandshakeIcon,
    },
    // Naslag en geen werk, maar wel een eigen scherm: de taxonomie hoort niet bij
    // het bijstellen (instellingen) en niet bij een periode (dashboard).
    { label: 'Hulpvragen', pad: '/hulpvragen', recht: 'taxonomie:read', icoon: ListTreeIcon },
  ];

export function AppSidebar() {
  const { user } = useAuth();
  const location = useLocation();
  const zichtbaar = ITEMS.filter((i) => mag(user?.rechten, i.recht));

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        {/* Het merk is een breed woordmerk en er is geen vierkant merkteken.
            In een ingeklapte balk past het niet, en een flex-kind krimpt
            standaard mee: vandaar `shrink-0` tegen het platduwen, en verbergen
            zodra de balk inklapt in plaats van het samengeperst te tonen. */}
        <div className="flex items-center px-2 py-1 group-data-[collapsible=icon]:hidden">
          <span className="shrink-0">
            <Logo height={32} />
          </span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {zichtbaar.map((item) => (
                <SidebarMenuItem key={item.pad}>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname.startsWith(item.pad)}
                    tooltip={item.label}
                  >
                    <NavLink to={item.pad}>
                      <item.icoon />
                      <span>{item.label}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
